import { $gray600 } from "../../cssjs/colors";
import { ISTATUS } from "../../models/common/CommonModels";

const TOTAL_Z2_CAPACITY = 250; // Total capacity percentage for occupancy
const TOTAL_SCREENING_MINUTES = 20;
const preScreeningLabel = "Pre-screening";

const ZONE_STATUS_COLORS: Record<ISTATUS, [string, string, string]> = {
  "": [$gray600, $gray600, $gray600], // Default/unknown
  good: ["#7BC67E", "#4CAF50", "#2E7D32"],
  moderate: ["#FFD580", "#FFA726", "#957600"],
  poor: ["#EF9A9A", "#EF5350", "#B71C1C"],
};

const STATUS_PRIORITY: Record<ISTATUS, number> = {
  good: 0,
  moderate: 1,
  poor: 2,
  "": -1,
};

/** Converts "mm:ss" to total minutes (decimal) */
export const mmssToMinutes = (mmss: string): number => {
  const [mm, ss] = mmss.split(":").map(Number);
  return mm + ss / 60;
};

export const getZoneIndex = (zoneName: string): number => {
  if (zoneName.includes("Overflow")) {
    return 1;
  } else if (zoneName.includes(preScreeningLabel)) {
    return 2;
  }
  return 3;
};

export interface IDonutSegment {
  resourceName: string;
  resourceTitle: string;
  resourceColor: string;
  resourceCount: number;
  resourceTotal: string | number;
  resourceSubtitle?: string;
  isZero?: boolean;
}

export const transformScreeningData = (
  zones: {
    zoneId: string;
    screeningTime: string;
    pax: number;
    status: ISTATUS;
  }[],
  totalScreeningTime: string
): IDonutSegment[] => {
  const segments: IDonutSegment[] = zones.map((zone) => {
    const roundedMinutes = Math.round(mmssToMinutes(zone.screeningTime));
    const isZero = roundedMinutes === 0;

    return {
      resourceName: `${zone.zoneId}`,
      resourceTitle: `${zone.zoneId}`,
      resourceSubtitle: `${zone.pax}`,
      resourceColor: getZoneStatusColor(zone.status, getZoneIndex(zone.zoneId)),
      resourceCount: isZero ? 1 : roundedMinutes,
      resourceTotal: totalScreeningTime,
      ...(isZero ? { isZero: true } : {}),
    };
  });

  const totalUsed = segments.reduce((sum, s) => sum + s.resourceCount, 0);
  const remaining = TOTAL_SCREENING_MINUTES - totalUsed;

  if (remaining > 0) {
    segments.push({
      resourceName: "Threshold",
      resourceTitle: "Threshold",
      resourceSubtitle: "-",
      resourceColor: "#e0e0e0",
      resourceCount: remaining,
      resourceTotal: TOTAL_SCREENING_MINUTES,
    });
  }

  return segments;
};

export const transformCapacityData = (
  capacity: {
    zoneId: string;
    currentPax: number;
    occupancyPercentage: number;
    status: ISTATUS;
  }[]
): IDonutSegment[] => {
  const segments: IDonutSegment[] = capacity.map((zone) => ({
    resourceName: `${zone.zoneId}`,
    resourceTitle: `${zone.zoneId}`,
    resourceSubtitle: `${zone.currentPax}`,
    resourceColor: getZoneStatusColor(zone.status, getZoneIndex(zone.zoneId)),
    resourceCount: zone.occupancyPercentage,
    resourceTotal: zone.occupancyPercentage,
  }));

  const totalUsed = segments.reduce((sum, s) => sum + s.resourceCount, 0);
  const remaining = Math.max(100 - totalUsed, 0);
  const remainingCapacity = Math.max(
    TOTAL_Z2_CAPACITY - capacity.reduce((sum, z) => sum + z.currentPax, 0),
    0
  );

  if (remaining > 0) {
    segments.push({
      resourceName: "Remaining",
      resourceTitle: "Remaining",
      resourceSubtitle: `${remainingCapacity}`,
      resourceColor: "#e0e0e0",
      resourceCount: remaining,
      resourceTotal: 100,
    });
  }

  return segments;
};

/** Extracts legend items only for screening zones (excludes "Remaining") */
export const getScreeningLegendItems = (
  zones: { zoneId: string; pax: number; status: ISTATUS }[] // Add status to type
): { resourceTitle: string; resourceColor: string }[] =>
  zones.map((zone) => {
    return {
      resourceTitle: `${zone.zoneId}`,
      resourceColor: getZoneStatusColor(zone.status, getZoneIndex(zone.zoneId)),
    };
  });

/**
 * Returns a color based on status and zone (1-3).
 * Zone 1 = lightest shade, Zone 3 = darkest shade.
 *
 */
export const getZoneStatusColor = (
  status: ISTATUS | undefined,
  zoneId: number
): string => {
  if (!status || !ZONE_STATUS_COLORS[status]) {
    return $gray600;
  }

  const zoneIndex = Math.min(Math.max(zoneId - 1, 0), 2); // Clamp to 0-2
  return ZONE_STATUS_COLORS[status][zoneIndex];
};

export const getPaxSecurityCardStatus = (
  status1: ISTATUS | undefined,
  status2: ISTATUS | undefined
): ISTATUS => {
  if (!status1) {
    return status2 ?? "";
  }
  if (!status2) {
    return status1 ?? "";
  }

  const priority1 = STATUS_PRIORITY[status1] ?? -1;
  const priority2 = STATUS_PRIORITY[status2] ?? -1;

  return priority1 >= priority2 ? status1 : status2;
};

export const extractPaxScreeningData = (apiData: any) => {
  if (!apiData) {
    return {
      cardStatus: "" as ISTATUS,
      screeningLegendItems: [],
      screeningDonutData: [],
      capacityDonutData: [],
      totalScreeningTime: "0",
      totalPax: 0,
    };
  }

  const kpis = Array.isArray(apiData.kpis) ? apiData.kpis : [];
  const screeningKpi = kpis.find((k: any) => k?.type === "screening");
  const capacityKpi = kpis.find((k: any) => k?.type === "capacity");

  const screeningStatus = (screeningKpi?.status as ISTATUS) ?? "";
  const capacityStatus = (capacityKpi?.status as ISTATUS) ?? "";

  const totalScreeningMinutes = screeningKpi?.kpiValue ?? 0;
  const roundedTotalScreeningMinutes = Math.round(totalScreeningMinutes);
  const totalScreeningTime = `${roundedTotalScreeningMinutes}`;
  const totalScreeningTimeWithUnit =
    roundedTotalScreeningMinutes <= 0 ? "<1m" : `${totalScreeningTime}m`;

  const cardStatus = getPaxSecurityCardStatus(screeningStatus, capacityStatus);

  //Zones
  const zones = Array.isArray(apiData.screeningInfo?.zones)
    ? apiData.screeningInfo.zones
    : [];

  const totalPax = Math.max(apiData.screeningInfo?.totalPax ?? 0, 0);
  const rawOccupancy = capacityKpi?.kpiValue ?? 0;
  const occupancyPercentage = Math.min(Math.max(rawOccupancy, 0), 100);

  const zonesWithStatus = zones.map((zone: any) => ({
    ...zone,
    status: screeningStatus,
  }));

  const screeningLegendItems = getScreeningLegendItems(zonesWithStatus);
  const screeningDonutData = transformScreeningData(
    zonesWithStatus,
    totalScreeningTimeWithUnit
  );

  const preScreeningPax =
    zones.find((z: any) => z.zoneId === preScreeningLabel)?.pax ?? 0;

  const capacityDonutData = transformCapacityData([
    {
      zoneId: preScreeningLabel,
      currentPax: preScreeningPax,
      occupancyPercentage,
      status: capacityStatus,
    },
  ]);

  return {
    cardStatus,
    screeningLegendItems,
    screeningDonutData,
    capacityDonutData,
    totalScreeningTime,
    totalPax,
  };
};
